import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

interface User {
  sub: string;
  email: string;
  role: string;          // 'super_admin' | 'admin' | 'member'
  companyId: string;
  companyName: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isLoading: boolean;
  login: (email: string, password: string, companySlug?: string) => Promise<User>;
  logout: () => void;
  refreshToken: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function userFromToken(token: string, companyName?: string): User | null {
  try {
    const p = JSON.parse(atob(token.split('.')[1]));
    if (p.exp * 1000 < Date.now()) return null;
    return {
      sub: p.sub,
      email: p.email,
      role: p.role,
      companyId: p.company_id,
      companyName: companyName ?? localStorage.getItem('company_name') ?? '',
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const u = token ? userFromToken(token) : null;
    if (!u) clearSession();
    else setUser(u);
    setIsLoading(false);
  }, []);

  const clearSession = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('company_name');
    setUser(null);
  };

  const login = async (email: string, password: string, companySlug?: string): Promise<User> => {
    const res = await fetch('/api/v1/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, company_slug: companySlug ?? null }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'Invalid email or password');
    }
    const data = await res.json();
    localStorage.setItem('access_token', data.access_token);
    localStorage.setItem('company_name', data.company?.name ?? '');
    const u = userFromToken(data.access_token, data.company?.name);
    if (!u) throw new Error('Invalid token');
    setUser(u);
    return u;
  };

  const logout = () => {
    const wasAdmin = user?.role === 'super_admin';
    clearSession();
    toast.success('Logged out');
    navigate(wasAdmin ? '/admin/login' : '/login');
  };

  const refreshToken = async () => {
    try {
      const res = await fetch('/api/v1/auth/me');
      if (!res.ok) return clearSession();
      const data = await res.json();
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('company_name', data.company?.name ?? '');
      setUser(userFromToken(data.access_token, data.company?.name));
    } catch {
      /* keep current session */
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isSuperAdmin: user?.role === 'super_admin',
        isLoading,
        login,
        logout,
        refreshToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
